import { createApp } from 'vue'
import ElementPlus, { ElNotification } from 'element-plus'
import en from 'element-plus/dist/locale/en.mjs'
import moment from 'moment'
import auth, { authPlugin } from './services/auth'
import { msalPlugin } from './services/msal'
import http from './services/http'
import bootstrapLite from './plugins/bootstrapLite'
import { setNotifier } from './services/notify'

// import ZfTheme from 'zf_vuejs_pkg/src/components'
// prod release commit
import { library, config, dom } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import ToggleButton from '@/components/shared/ToggleButton'

import App from './App.vue'
import { createRouter } from './router'
import store from './store'

import 'es6-promise/auto'
import 'element-plus/dist/index.css'
import 'vue-good-table-next/dist/vue-good-table-next.css'
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css'
import './assets/scss/style.scss'
import VueVirtualScroller from 'vue-virtual-scroller'
// Add fontawesome icons globally
import {
  faHome,
  faTable,
  faTrophy,
  faChild,
  faArrowsAlt,
  faShareAlt,
  faAddressCard,
  faSearch,
  faSave,
  faDownload,
  faPlus,
  faEdit,
  faTrashAlt,
  faEye,
  faUser,
  faKey,
  faTachometerAlt,
  faCubes,
  faSyncAlt,
  faDesktop,
  faServer,
  faFileAlt,
  faFileWord,
  faFileExcel,
  faFilePowerpoint,
  faFilePdf,
  faHashtag,
  faBuilding,
  faBook,
  faBookmark,
  faBriefcase,
  faTag,
  faIdBadge,
  faGlobeEurope,
  faTimes,
  faUsers,
  faFlask,
  faClipboardList,
  faPencilRuler,
  faPenSquare,
  faPencilAlt,
  faTools,
  faLayerGroup,
  faClipboardCheck,
  faChevronRight,
  faChevronDown,
  faChevronUp,
  faCaretDown,
  faCaretUp,
  faChevronLeft,
  faAngleDown,
  faAngleUp,
  faBars,
  faVial,
  faCheckCircle,
  faExclamationCircle,
  faChartBar,
  faExclamation,
  faTimesCircle,
  faInfo,
  faCheck,
  faMinus,
  faExternalLinkAlt,
  faExclamationTriangle,
  faExternalLinkSquareAlt,
  faInfoCircle,
  faCloudUploadAlt,
  faExpand,
  faCompress,
  faCloud,
  faSortDown,
  faSortUp,
  faSort,
  faPauseCircle,
  faCircleNotch
} from '@fortawesome/free-solid-svg-icons'

if (process.env.NODE_ENV === 'development') {
  window.addEventListener('error', (event) => {
    if (event?.message?.includes('ResizeObserver loop')) {
      event.stopImmediatePropagation()
    }
  })
}

library.add(
  faHome,
  faTable,
  faTrophy,
  faChild,
  faArrowsAlt,
  faExpand,
  faCompress,
  faShareAlt,
  faAddressCard,
  faCloudUploadAlt,
  faFileAlt,
  faFileWord,
  faFileExcel,
  faInfoCircle,
  faCloud,
  faExternalLinkAlt,
  faExternalLinkSquareAlt,
  faFilePowerpoint,
  faFilePdf,
  faSearch,
  faPlus,
  faTimes,
  faDownload,
  faSave,
  faEdit,
  faTrashAlt,
  faEye,
  faUser,
  faKey,
  faTachometerAlt,
  faCubes,
  faSyncAlt,
  faDesktop,
  faServer,
  faHashtag,
  faBuilding,
  faBook,
  faBookmark,
  faBriefcase,
  faTag,
  faIdBadge,
  faGlobeEurope,
  faUsers,
  faFlask,
  faClipboardList,
  faClipboardCheck,
  faPencilRuler,
  faPenSquare,
  faPencilAlt,
  faTools,
  faChevronLeft,
  faChevronRight,
  faChevronUp,
  faChevronDown,
  faCaretDown,
  faCaretUp,
  faAngleDown,
  faAngleUp,
  faVial,
  faBars,
  faExclamation,
  faCheckCircle,
  faExclamationCircle,
  faExclamationTriangle,
  faTimesCircle,
  faLayerGroup,
  faChartBar,
  faCheck,
  faMinus,
  faSortDown,
  faSortUp,
  faSort,
  faInfo,
  faPauseCircle,
  faCircleNotch
)

config.autoReplaceSvg = 'nest'

dom.watch()

const initializeVue = async () => {
  const router = await createRouter()
  router.beforeEach(async (to, from, next) => {
    if (!auth.ready()) {
      await auth.init()
    }
    const nearestWithTitle = to.matched.slice().reverse().find(r => r.meta && r.meta.title)
    if (nearestWithTitle) document.title = nearestWithTitle.meta.title
    if (to.name === 'login' && auth.check()) {
      next({ name: 'home' })
      return
    }
    if (to.meta && to.meta.auth && !auth.check()) {
      // Remember where they were headed. Links from other systems (e.g. a
      // shortcut into /search?pace_number=...) usually arrive without a live
      // session, and dropping the target here would land them on Home with
      // the query lost. Login sends them on afterwards.
      const isHome = to.path === '/' || to.name === 'home'
      next({ name: 'login', query: isHome ? {} : { next: to.fullPath } })
      return
    }
    next()
  })

  moment.locale('en-GB')

  await auth.init()

  const app = createApp(App)
  app.config.globalProperties.$env = process.env
  app.config.globalProperties.moment = moment
  app.config.globalProperties.$set = (target, key, value) => {
    target[key] = value
  }
  app.config.globalProperties.$http = http
  app.config.globalProperties.$axios = http
  app.config.globalProperties.axios = http

  app.use(store)
  app.use(router)
  app.use(ElementPlus, { locale: en })
  app.use(VueVirtualScroller)
  app.use(authPlugin)
  app.use(msalPlugin)
  app.use(bootstrapLite)
  app.component('font-awesome-icon', FontAwesomeIcon)
  app.component('ToggleButton', ToggleButton)
  setNotifier(ElNotification)

  app.mount('#app')
}

initializeVue()
